import { OPERATIONS, REDIRECT_URLS } from '../constants';
import { axiosInstance } from '../api/api';
import { store } from '../redux/store';

/**
 * Utility functions for managing operation-based flow
 */

/**
 * Navigable ops for ordering / "next" (excludes TAKE_IN_OUT).
 * MACHINE is excluded only when ENGRAVING is present (engrave flow skips machine in nav).
 * MACHINE-only stations must still resolve to /assembly/machine.
 */
export const getNavigableOperations = (allowedOperations = []) => {
	const stripMachineForEngrave = allowedOperations.includes(
		OPERATIONS.ENGRAVING,
	);

	// The simple station ops that trigger MACHINE embedding.
	// MACHINE is stripped from navigable only when one of THESE ops is present —
	// NOT for machine-only stations, RECEIVE_PART, RECEIVE_SN, SCAN, ENGRAVE, etc.
	const EMBEDDED_MACHINE_OPS = [
		OPERATIONS.WAHA,
		OPERATIONS.RBT,
		OPERATIONS.BODY_ALIGN,
		OPERATIONS.DT,
		OPERATIONS.PAINT_SELECT,
		OPERATIONS.SHOWER,
		OPERATIONS.PRE_CAL,
		OPERATIONS.SFLT,
		OPERATIONS.QUALITY,
		OPERATIONS.WCPA,
		OPERATIONS.WTCL,
		OPERATIONS.TRANSFER_DROP,
		OPERATIONS.MAJOR_REPAIR,
	];
	const stripMachineForEmbedded = allowedOperations.some((op) =>
		EMBEDDED_MACHINE_OPS.includes(op),
	);

	// When WTCL is present, QUALITY is redundant — WTCL page already shows
	// the same defect table + close defect + mark WTCL, and calling
	// POST /station/complete for QUALITY causes API failures in this combo.
	const stripQualityForWtcl = allowedOperations.includes(OPERATIONS.WTCL);

	const navigable = allowedOperations.filter((op) => {
		if (op === OPERATIONS.TAKE_IN_OUT) return false;
		if (op === OPERATIONS.MACHINE && (stripMachineForEngrave || stripMachineForEmbedded)) return false;
		if (op === OPERATIONS.QUALITY && stripQualityForWtcl) return false;
		return true;
	});

	if (
		navigable.length === 0 &&
		allowedOperations.includes(OPERATIONS.MACHINE)
	) {
		return [OPERATIONS.MACHINE];
	}
	return navigable;
};

/**
 * Determines the next operation based on completed operations and allowed operations
 * @param {Array} completedOperations - Array of completed operations
 * @param {Array} allowedOperations - Array of allowed operations for the user
 * @returns {string} - Next operation to perform
 */
export const getNextOperation = (completedOperations = [], allowedOperations = []) => {
	const navigableOperations = getNavigableOperations(allowedOperations);

	// Always start from RECEIVE_SN when available, regardless of operation order from token.
	if (!completedOperations.length && navigableOperations.includes(OPERATIONS.RECEIVE_SN)) {
		return OPERATIONS.RECEIVE_SN;
	}

	// If no operations completed, start with first navigable operation.
	if (!completedOperations.length) {
		return navigableOperations[0];
	}

	// If all required operations completed:
	// - go to RECEIVE_SN when that operation exists
	// - for WCPA-only stations, stay on WCPA (lot-based, no per-part cycle)
	// - otherwise go to RECEIVE_PART for next part flow
	if (
		navigableOperations.every((op) => completedOperations.includes(op))
	) {
		if (navigableOperations.includes(OPERATIONS.RECEIVE_SN)) {
			return OPERATIONS.RECEIVE_SN;
		}
		if (navigableOperations.length === 1 && navigableOperations[0] === OPERATIONS.WCPA) {
			return OPERATIONS.WCPA;
		}
		return OPERATIONS.RECEIVE_PART;
	}

	// Find the first incomplete navigable operation.
	return navigableOperations.find(
		(operation) => !completedOperations.includes(operation),
	);
};

/**
 * Gets the current part status from the API
 * @returns {Promise<Object>} - Current part data including completed operations
 */
export const getCurrentPartStatus = async () => {
	try {
		const state = store.getState();
		const allowedOperations = state.operations?.allowedOperations || [];
		const isMrStation = allowedOperations.includes(OPERATIONS.MAJOR_REPAIR);
		const { data } = await axiosInstance.get(isMrStation ? '/mr/station/current-part-on-mr' : '/station/current-part');
		if (data && isMrStation) {
			data.serialNumber = data.serialNo || data.serialNumber;
			data.sequenceNumber = data.asn || data.sequenceNumber;
		}
		return data || {};
	} catch (error) {
		console.error('Error fetching current part status:', error);
		return {};
	}
};

/**
 * Determines if user should be redirected based on current operation status
 * @param {Object} currentPart - Current part data
 * @param {Array} allowedOperations - User's allowed operations
 * @param {string} currentPath - Current route path
 * @returns {Object} - Redirect information { shouldRedirect: boolean, targetPath: string, operation: string }
 */
export const shouldRedirectBasedOnOperation = (
	currentPart,
	allowedOperations,
	currentPath,
) => {
	const completedOperations = currentPart?.completedOperations || [];
	const nextOperation = getNextOperation(
		completedOperations,
		allowedOperations,
	);

	// If no current part or empty response, start with first operation.
	if (!currentPart || !Object.keys(currentPart).length) {
		const firstOperation = getNextOperation([], allowedOperations) || allowedOperations[0];
		const firstPath = REDIRECT_URLS[firstOperation] || '/scan-vin';

		return {
			shouldRedirect: currentPath !== firstPath,
			targetPath: firstPath,
			operation: firstOperation
		};
	}

	// For ENGRAVE stations: if a current part exists (has a serialNumber), always
	// redirect to the engraving screen regardless of completedOperations state.
	if (
		allowedOperations.includes(OPERATIONS.ENGRAVING) &&
		currentPart?.serialNumber
	) {
		const engravePath = REDIRECT_URLS[OPERATIONS.ENGRAVING];
		return {
			shouldRedirect: currentPath !== engravePath,
			targetPath: engravePath,
			operation: OPERATIONS.ENGRAVING
		};
	}

	// If current path doesn't match the expected operation path, redirect
	const expectedPath = REDIRECT_URLS[nextOperation];
	if (expectedPath && currentPath !== expectedPath) {
		return {
			shouldRedirect: true,
			targetPath: expectedPath,
			operation: nextOperation
		};
	}

	return {
		shouldRedirect: false,
		targetPath: currentPath,
		operation: nextOperation
	};
};

/**
 * Updates the current line item with completed operations
 * @param {Object} currentLineItem - Current line item data
 * @param {string} completedOperation - Operation that was just completed
 * @returns {Promise<Object>} - Updated line item data
 */
export const updateCompletedOperations = async (currentLineItem, completedOperation) => {
	try {
		// Get fresh data from API
		const currentPartData = await getCurrentPartStatus();

		// Merge completed operations
		const updatedCompletedOperations = [
			...new Set([
				...(currentLineItem?.completedOperations || []),
				...(currentPartData?.completedOperations || []),
				completedOperation
			])
		];

		return {
			...currentLineItem,
			...currentPartData,
			completedOperations: updatedCompletedOperations
		};
	} catch (error) {
		console.error('Error updating completed operations:', error);
		// Fallback to local update
		return {
			...currentLineItem,
			completedOperations: [
				...new Set([
					...(currentLineItem?.completedOperations || []),
					completedOperation
				])
			]
		};
	}
};

/**
 * Operation completion handler that updates state and determines next action
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} navigate - React Router navigate function
 * @param {Object} currentLineItem - Current line item data
 * @param {string} completedOperation - Operation that was just completed
 * @param {Array} allowedOperations - User's allowed operations
 * @param {Function} updateCurrentLineItem - Redux action to update current line item
 * @param {Function} updateOperations - Redux action to update operations
 */
export const handleOperationCompletion = async (
	dispatch,
	navigate,
	currentLineItem,
	completedOperation,
	allowedOperations,
	updateCurrentLineItem,
	updateOperations,
) => {
	try {
		// Update the completed operations
		const updatedLineItem = await updateCompletedOperations(
			currentLineItem,
			completedOperation,
		);

		// Update Redux state
		dispatch(updateCurrentLineItem(updatedLineItem));

		// Determine next operation
		const nextOperation = getNextOperation(
			updatedLineItem.completedOperations,
			allowedOperations,
		);

		// Update current operation in Redux
		dispatch(updateOperations({ currentOperation: nextOperation }));

		// Navigate to next screen
		const nextPath = REDIRECT_URLS[nextOperation] || '/scan-vin';
		navigate(nextPath);

		return {
			success: true,
			nextOperation,
			nextPath,
			updatedLineItem,
		};
	} catch (error) {
		console.error('Error handling operation completion:', error);
		return {
			success: false,
			error: error.message,
		};
	}
};

/**
 * Validates if user has permission for a specific operation
 * @param {string} operation - Operation to check
 * @param {Array} allowedOperations - User's allowed operations
 * @returns {boolean} - Whether user has permission
 */
export const hasOperationPermission = (operation, allowedOperations = []) => {
	return allowedOperations.includes(operation);
};

/**
 * Gets operation display name
 * @param {string} operation - Operation code
 * @returns {string} - Display name for the operation
 */
export const getOperationDisplayName = (operation) => {
	const operationNames = {
		[OPERATIONS.RECEIVE_SN]: 'Receive Serial Number',
		[OPERATIONS.RECEIVE_PART]: 'Receive Part',
		[OPERATIONS.PART_VALIDATION]: 'Part Validation',
		[OPERATIONS.PROCESS_VALIDATION]: 'Process Validation',
		[OPERATIONS.BOOK]: 'Book',
		[OPERATIONS.ENGRAVING]: 'Engraving',
		[OPERATIONS.MACHINE]: 'Machine',
		[OPERATIONS.TAKE_IN_OUT]: 'Take In/Out',
		[OPERATIONS.DROP]: 'Drop',
		[OPERATIONS.WTCL]: 'WTCL',
		[OPERATIONS.WCPA]: 'WCPA Audit', 
		[OPERATIONS.SFLT]: 'SFLT',
		[OPERATIONS.BODY_ALIGN]: 'Body Align',
		[OPERATIONS.WAHA]: 'Waha',
		[OPERATIONS.RBT]: 'Rbt',
		[OPERATIONS.PAINT_SELECT]: 'Paint Select',
		[OPERATIONS.PRE_CAL]: 'Pre Cal',
		[OPERATIONS.DT]: 'Dt',
		[OPERATIONS.SHOWER]: 'Shower',
		[OPERATIONS.MAJOR_REPAIR]: 'Major Repair',
	};

	return operationNames[operation] || operation;
};
